import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateExpense,
  CreateExpenseDraft,
  Expense,
  ExpenseCategory,
  RecordExpenseReimbursement,
  UpdateExpense,
} from '@scrambl/shared';
import { toast } from 'sonner';

import { expensesApi } from '@/lib/api/expenses.api';

import { queryKeys } from '../query-keys';

const invalidateExpenseQueries = (queryClient: ReturnType<typeof useQueryClient>, expense?: Expense | null) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all });

  if (!expense) {
    return;
  }

  queryClient.invalidateQueries({ queryKey: queryKeys.expenses.detail(expense.id) });
  queryClient.invalidateQueries({ queryKey: queryKeys.expenses.tabCounts(expense.id) });
};

export function useCreateExpenseMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateExpense) => expensesApi.createExpense(data),
    onSuccess: (expense) => {
      invalidateExpenseQueries(queryClient, expense);
      toast.success('Expense draft created');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create expense');
    },
  });
}

export function useCreateExpenseDraftMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateExpenseDraft = {}) => expensesApi.createExpenseDraft(data),
    onSuccess: (expense) => {
      invalidateExpenseQueries(queryClient, expense);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to start expense draft');
    },
  });
}

export function useUpdateExpenseMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateExpense }) => expensesApi.updateExpense(id, data),
    onSuccess: (expense) => {
      invalidateExpenseQueries(queryClient, expense);
      toast.success('Expense updated');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update expense');
    },
  });
}

export function useDeleteExpenseMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => expensesApi.deleteExpense(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all });
      toast.success('Expense deleted');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete expense');
    },
  });
}

function useExpenseActionMutation(
  mutationFn: (id: string, payload?: string | RecordExpenseReimbursement) => Promise<Expense>,
  successMessage: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: string | RecordExpenseReimbursement }) =>
      mutationFn(id, payload),
    onSuccess: (expense) => {
      invalidateExpenseQueries(queryClient, expense);
      toast.success(successMessage);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Expense action failed');
    },
  });
}

export function useSubmitExpenseMutation() {
  return useExpenseActionMutation((id) => expensesApi.submitExpense(id), 'Expense submitted');
}

export function useRecallExpenseMutation() {
  return useExpenseActionMutation((id) => expensesApi.recallExpense(id), 'Expense recalled');
}

export function useApproveExpenseMutation() {
  return useExpenseActionMutation((id) => expensesApi.approveExpense(id), 'Expense approved');
}

export function useRequestExpenseChangesMutation() {
  return useExpenseActionMutation((id, payload) => expensesApi.requestExpenseChanges(id, typeof payload === 'string' ? payload : undefined), 'Changes requested');
}

export function useRejectExpenseMutation() {
  return useExpenseActionMutation((id, payload) => expensesApi.rejectExpense(id, typeof payload === 'string' ? payload : undefined), 'Expense rejected');
}

export function useRecordExpenseReimbursementMutation() {
  return useExpenseActionMutation(
    (id, payload) => expensesApi.recordReimbursement(id, payload as RecordExpenseReimbursement),
    'Reimbursement recorded',
  );
}

export function useCreateExpenseCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<ExpenseCategory> & { name: string }) => expensesApi.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.categories() });
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create expense category');
    },
  });
}

export function useUpdateExpenseCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ExpenseCategory> }) => expensesApi.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.categories() });
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update expense category');
    },
  });
}

export function useDeleteExpenseCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => expensesApi.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.categories() });
      queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete expense category');
    },
  });
}
